import {
    Component,
    EventEmitter,
    Input,
    Output,
    OnChanges,
    SimpleChanges,
    inject,
} from '@angular/core';
import { AuthService } from '@services';
import {
    DxButtonModule,
    DxDropDownButtonModule,
    DxToolbarModule,
} from 'devextreme-angular';
import { AccessType, CreditRequestAction, CreditRequestStatus, PermissionType } from '@services/web-api-client';

interface MenuAction {
    id: CreditRequestAction;
    text: string;
    icon: string;
}

@Component({
    selector: 'app-credit-request-details-toolbar',
    standalone: true,
    imports: [DxToolbarModule, DxButtonModule, DxDropDownButtonModule],
    templateUrl: './credit-request-details-toolbar.component.html',
})
export class CreditRequestDetailsToolbarComponent implements OnChanges {
    @Input() number: string | null = null;
    @Input() status: CreditRequestStatus | undefined;
    @Input() actions: CreditRequestAction[] | null = null;
    @Input() isReadOnly: boolean = false;
    @Input() isSubmitting: boolean = false;
    @Input() isLoading: boolean = false;
    @Input() isExporting: boolean = false;

    @Output() cancelClick = new EventEmitter<void>();
    @Output() save = new EventEmitter<CreditRequestAction>();
    @Output() exportClick = new EventEmitter<void>();

    protected auth = inject(AuthService);
    protected moreMenuActions: MenuAction[] = [];
    public CreditRequestAction = CreditRequestAction;

    private readonly allMenuActions: MenuAction[] = [
        {
            id: CreditRequestAction.Cancel,
            text: 'Cancel',
            icon: 'fa-thin fa-x',
        },
    ];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['status'] || changes['actions']) {
            this.buildMenuActions();
        }
    }

    private buildMenuActions(): void {
        if (this.isLoading) {
            this.moreMenuActions = [];
            return;
        }

        this.moreMenuActions = this.allMenuActions.filter((action) =>
            this.canShowButton(action.id),
        );
    }

    canShowButton(action: CreditRequestAction): boolean {
        if (this.isLoading) return false;

        if (!this.number && !this.isReadOnly) {
            return ((action === CreditRequestAction.Submit && this.canSubmit) || action === CreditRequestAction.SaveAsDraft);
        }

        if (this.number && !this.isReadOnly && action === CreditRequestAction.Save) {
            return true;
        }

        return this.actions?.includes(action) ?? false;
    }

    get canSubmit (): boolean {
        return this.auth.checkPermission(PermissionType.CreditRequestSubmit, AccessType.Edit);
    }

    get canExport(): boolean {
        return !this.isLoading && !!this.number && !!this.status && this.status !== CreditRequestStatus.Draft;
    }

    get showMoreMenu(): boolean {
        return this.number !== null && this.moreMenuActions.length > 0;
    }

    onMenuActionClick(e: { itemData?: MenuAction }): void {
        if (!e.itemData) return;

        this.onButtonClick(e.itemData.id as CreditRequestAction);
    }

    onButtonClick(status: CreditRequestAction) {
        this.save.emit(status);
    }
}
